import React from 'react';
import { SystemService } from 'services';
import { Pagination } from 'components';
import moment from 'moment';

// ============================================================
// Shapes reales del backend (spec C4 v2).
// El backend devuelve TODAS las líneas filtradas en `lines` y ya
// no manda `pagination`. La paginación es client-side por EVENTOS
// agrupados (no por líneas), así que tipamos localmente solo lo
// que realmente consumimos.
// ============================================================

type LogFile = 'error' | 'out';
type LogLevel = 'error' | 'warn' | 'info' | 'log' | string;
type LogOrder = 'asc' | 'desc';

interface Pm2LogLine {
	line_number?: number;
	timestamp?: string | null;
	level?: LogLevel;
	raw?: string;
	preview?: string;
}

interface Pm2LogsFileInfo {
	path?: string;
	size_bytes?: number;
	size_mb?: number;
	last_modified?: string;
}

interface Pm2LogsFull {
	lines?: Pm2LogLine[];
	total_returned?: number;
	total_in_file?: number;
	order?: LogOrder;
	filters_applied?: {
		file?: string;
		search?: string | null;
		date_from?: string | null;
		date_to?: string | null;
		order?: LogOrder;
		max_lines?: number;
	};
	file_info?: Pm2LogsFileInfo;
}

// Helpers de bajo nivel reutilizados por la agrupación y el render.
// Vivían más abajo, los subimos porque `fingerprint` (dedupe) los
// necesita y queremos un orden de declaración limpio sin TDZ.
// eslint-disable-next-line no-control-regex
const ANSI_REGEX = /\x1b\[[0-9;]*m/g;

const stripAnsi = (s: string): string => (s ? s.replace(ANSI_REGEX, '') : '');

// ============================================================
// Agrupación de líneas por evento. Una línea con timestamp != null
// abre un grupo nuevo (parent); las siguientes con timestamp == null
// se cuelgan como children (típicamente stack traces de Nest).
//
// IMPORTANTE: la agrupación SIEMPRE se hace sobre el orden natural
// del archivo (ASC). En DESC el backend nos manda las líneas al revés,
// así que invertimos antes de agrupar y reinvertimos los grupos al
// final. De lo contrario un padre en DESC quedaba "adoptando" las
// líneas que en el archivo natural venían ANTES (sus hijas tendrían
// line_number MENOR que el padre, que es absurdo: un stack trace
// siempre viene en líneas con number > el del header del error).
//
// Dentro de cada grupo las children quedan en orden natural del
// archivo (más antigua → más reciente del stack), porque eso es
// lo que se lee bien siempre, independiente del order global.
// ============================================================

interface LogEvent {
	parent: Pm2LogLine;     // siempre presente (la primera línea del grupo, padre con timestamp)
	children: Pm2LogLine[]; // 0..N líneas de continuación sin timestamp, en orden natural ASC
	// Dedupe consecutivo (Issue 4):
	duplicateCount: number;       // ≥1. 1 = único; >1 = el padre se repite consecutivamente
	allTimestamps: (string | null | undefined)[]; // timestamps de TODOS los padres deduplicados (orden ASC del archivo)
}

const groupLines = (lines: Pm2LogLine[], order: LogOrder): LogEvent[] => {
	// 1) Si DESC, invertir a ASC para que la heurística "padre con
	// timestamp + hijas siguientes sin timestamp" funcione respecto
	// al orden natural del archivo.
	const linesAsc = order === 'desc' ? [...lines].reverse() : lines;

	// 2) Agrupar en ASC.
	const groupsAsc: LogEvent[] = [];
	for (const line of linesAsc) {
		const hasTimestamp = !!line.timestamp;
		if (hasTimestamp || groupsAsc.length === 0) {
			// Nueva entrada: tiene timestamp, o es la primera y va sin
			// timestamp — la promovemos a parent de su propio grupo
			// (huérfano al inicio del archivo).
			groupsAsc.push({
				parent: line,
				children: [],
				duplicateCount: 1,
				allTimestamps: [line.timestamp],
			});
		}
		else {
			// Continuación del grupo abierto actual.
			groupsAsc[groupsAsc.length - 1].children.push(line);
		}
	}

	// 3) Si DESC, reinvertir el array de grupos (NO sus children).
	return order === 'desc' ? groupsAsc.reverse() : groupsAsc;
}

// ============================================================
// Dedupe consecutivo (Issue 4).
//
// Mismo mensaje repetido N veces seguidas (típico de errores
// transitorios que disparan una vez por request) colapsa a UN solo
// grupo con badge "×N". El conteo se calcula sobre el array de
// grupos YA en el orden final de presentación (post-reverse en DESC),
// así que "consecutivo" significa "consecutivo en la pantalla" y no
// se rompe por dirección de orden.
//
// El fingerprint normaliza el mensaje del padre para que dos eventos
// "iguales pero distinto worker/timestamp embebido" se consideren
// el mismo. Concretamente ignoramos:
//   - el header de Nest: `[Nest] 547010 - 05/22/2026, 7:11:11 PM`
//   - números largos (PIDs, IDs específicos) → token "N"
// El badge level y el timestamp del wrapper externo NO entran al
// fingerprint, porque el shape ya lo aísla.
// ============================================================

const NEST_HEADER_RE = /\[Nest\]\s+\d+\s*-\s*\d{1,2}\/\d{1,2}\/\d{4},\s*\d{1,2}:\d{2}:\d{2}\s*(AM|PM)?/gi;
const LONG_NUMBER_RE = /\b\d{3,}\b/g;

const fingerprint = (line?: Pm2LogLine): string => {
	if (!line) return '';
	const raw = stripAnsi(line.raw || line.preview || '');
	if (!raw) return '';
	return raw
		.replace(NEST_HEADER_RE, '')
		.replace(LONG_NUMBER_RE, 'N')
		.trim();
}

const deduplicateConsecutive = (groups: LogEvent[]): LogEvent[] => {
	const result: LogEvent[] = [];
	for (const group of groups) {
		const last = result[result.length - 1];
		const sameMessage = !!last && fingerprint(last.parent) === fingerprint(group.parent);
		if (sameMessage && last) {
			last.duplicateCount += 1;
			last.allTimestamps.push(group.parent.timestamp);
		}
		else {
			// Cloning superficial para no mutar el grupo del input.
			result.push({
				parent: group.parent,
				children: group.children,
				duplicateCount: 1,
				allTimestamps: [group.parent.timestamp],
			});
		}
	}
	return result;
}

// Cuántas children mostramos sin expandir cuando el grupo es "grande".
// Regla del spec: total ≤ 3 → todo visible; total > 3 → padre + 2 children + toggle.
const COLLAPSED_CHILD_COUNT = 2;
const GROUP_TOTAL_THRESHOLD = 3;

// Paginación CLIENT-SIDE: agrupamos los `lines` recibidos del backend
// en eventos y mostramos N eventos por página. La paginación NO viaja
// al backend — el backend ya filtró + ordenó todo.
// Default bajo (5) para leer errores con calma. El usuario puede subir
// con el selector "Eventos por página".
const EVENTS_PER_PAGE_OPTIONS = [5, 10, 20, 50];
const DEFAULT_EVENTS_PER_PAGE = 5;

// Opciones de "max_lines" que el frontend pide al backend (cuántas
// líneas leer del archivo). Distinto de EVENTS_PER_PAGE.
const MAX_LINES_OPTIONS = [1000, 5000, 10000, 20000];
const DEFAULT_MAX_LINES = 10000;
const DEFAULT_ORDER: LogOrder = 'desc';

const REFRESH_INTERVAL_MS = 15 * 1000;
const SEARCH_DEBOUNCE_MS = 500;

const levelClass = (level?: LogLevel): 'error' | 'warn' | 'info' | 'log' | 'unknown' => {
	if (!level) return 'unknown';
	const l = String(level).toLowerCase();
	if (l === 'error' || l === 'err') return 'error';
	if (l === 'warn' || l === 'warning') return 'warn';
	if (l === 'info') return 'info';
	if (l === 'log' || l === 'debug') return 'log';
	return 'unknown';
}

const formatBytes = (mb?: number): string => {
	const v = Number(mb);
	if (!isFinite(v)) return '—';
	if (v >= 1024) return `${ (v / 1024).toFixed(2) } GB`;
	return `${ v.toFixed(2) } MB`;
}

const formatNumber = (v: any): string => {
	const n = Number(v);
	if (isNaN(n)) return '0';
	return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

interface State {
	data: Pm2LogsFull | null;
	loadError: boolean;
	initialLoading: boolean;
	lastUpdated: Date | null;
	// filtros que viajan al backend
	file: LogFile;
	search: string;       // valor del input (controlado)
	searchApplied: string; // valor enviado al backend
	dateFrom: string;
	dateTo: string;
	order: LogOrder;      // asc | desc — qué orden pedimos al backend
	maxLines: number;     // cuántas líneas pedimos al backend
	// paginación CLIENT-SIDE (sobre grupos/eventos, no líneas)
	page: number;
	eventsPerPage: number; // cuántos eventos mostramos por página
	// grupos expandidos manualmente (índice dentro del array de grupos
	// del render actual). El Set NO se persiste cross-refresh ni cross-
	// reload — al cambiar filtros/tab/página se resetea.
	expandedGroups: Set<number>;
}

class Logs extends React.Component<{}, State> {

	refreshTimer: any = null;
	searchDebounceTimer: any = null;
	isMounted_: boolean = false;

	state: State = {
		data: null,
		loadError: false,
		initialLoading: true,
		lastUpdated: null,
		file: 'error',
		search: '',
		searchApplied: '',
		dateFrom: '',
		dateTo: '',
		order: DEFAULT_ORDER,
		maxLines: DEFAULT_MAX_LINES,
		page: 1,
		eventsPerPage: DEFAULT_EVENTS_PER_PAGE,
		expandedGroups: new Set<number>(),
	}

	componentDidMount() {
		this.isMounted_ = true;
		this.load(true);

		this.refreshTimer = setInterval(() => {
			this.load(false);
		}, REFRESH_INTERVAL_MS);
	}

	componentWillUnmount() {
		this.isMounted_ = false;
		if (this.refreshTimer) clearInterval(this.refreshTimer);
		if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
		this.refreshTimer = null;
		this.searchDebounceTimer = null;
	}

	// ---------- Carga ----------

	buildParams = () => {
		const { file, searchApplied, dateFrom, dateTo, order, maxLines } = this.state;
		// page/limit ya NO se envían: la paginación es client-side por
		// eventos agrupados. El backend devuelve todas las líneas filtradas.
		const params: any = {
			file,
			order,
			max_lines: maxLines,
			withoutLoading: true,
		};
		if (searchApplied) params.search = searchApplied;
		if (dateFrom) params.date_from = dateFrom;
		if (dateTo) params.date_to = dateTo;
		return params;
	}

	load = async (isFirst: boolean) => {
		try {
			const res: any = await SystemService.admin.getPm2Logs(this.buildParams());
			if (!this.isMounted_) return;
			this.setState({
				data: res || null,
				loadError: false,
				lastUpdated: new Date(),
				initialLoading: isFirst ? false : this.state.initialLoading,
			});
		}
		catch (e) {
			if (!this.isMounted_) return;
			this.setState({
				loadError: true,
				initialLoading: isFirst ? false : this.state.initialLoading,
			});
		}
	}

	manualRefresh = () => {
		if (this.refreshTimer) clearInterval(this.refreshTimer);
		this.load(false);
		this.refreshTimer = setInterval(() => this.load(false), REFRESH_INTERVAL_MS);
	}

	// ---------- Handlers de filtros ----------

	// Cualquier cambio de contexto (tab, filtros, página, orden, maxLines)
	// invalida los índices de los grupos expandidos manualmente —
	// los reseteamos.
	resetExpanded = (): Set<number> => new Set<number>();

	switchFile = (file: LogFile) => {
		if (file === this.state.file) return;
		this.setState({ file, page: 1, expandedGroups: this.resetExpanded() }, () => this.load(false));
	}

	onSearchChange = (value: string) => {
		this.setState({ search: value });
		if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
		this.searchDebounceTimer = setTimeout(() => {
			if (!this.isMounted_) return;
			this.setState({ searchApplied: value, page: 1, expandedGroups: this.resetExpanded() }, () => this.load(false));
		}, SEARCH_DEBOUNCE_MS);
	}

	onDateChange = (key: 'dateFrom' | 'dateTo', value: string) => {
		this.setState({ [key]: value, page: 1, expandedGroups: this.resetExpanded() } as any, () => this.load(false));
	}

	onOrderChange = (order: LogOrder) => {
		if (order === this.state.order) return;
		this.setState({ order, page: 1, expandedGroups: this.resetExpanded() }, () => this.load(false));
	}

	onMaxLinesChange = (maxLines: number) => {
		if (maxLines === this.state.maxLines) return;
		this.setState({ maxLines, page: 1, expandedGroups: this.resetExpanded() }, () => this.load(false));
	}

	// Cambio de tamaño de página: NO recarga del backend (la paginación
	// es client-side), solo recalcula la ventana visible. Reset page=1
	// porque el offset actual queda sin sentido al cambiar el size, y
	// reset expandedGroups porque los índices del Set son posicionales
	// respecto a la página actual.
	onEventsPerPageChange = (eventsPerPage: number) => {
		if (eventsPerPage === this.state.eventsPerPage) return;
		this.setState({ eventsPerPage, page: 1, expandedGroups: this.resetExpanded() });
	}

	clearFilters = () => {
		if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
		this.setState({
			search: '',
			searchApplied: '',
			dateFrom: '',
			dateTo: '',
			order: DEFAULT_ORDER,
			maxLines: DEFAULT_MAX_LINES,
			page: 1,
			eventsPerPage: DEFAULT_EVENTS_PER_PAGE,
			expandedGroups: this.resetExpanded(),
		}, () => this.load(false));
	}

	goToPage = (page: number) => {
		if (page < 1) return;
		// Paginación client-side: NO recargamos data, solo cambiamos
		// la ventana de grupos visible. Reseteamos expanded porque los
		// índices del Set son relativos al render actual.
		this.setState({ page, expandedGroups: this.resetExpanded() });
	}

	toggleGroup = (groupIdx: number) => {
		const next = new Set(this.state.expandedGroups);
		if (next.has(groupIdx)) next.delete(groupIdx);
		else next.add(groupIdx);
		this.setState({ expandedGroups: next });
	}

	// ---------- Render ----------

	renderHeader() {
		const { lastUpdated, initialLoading } = this.state;
		const lastLabel = lastUpdated
			? moment(lastUpdated).utcOffset(-4).format('HH:mm:ss')
			: '—';

		return (
			<div className="logs-header">
				<div className="logs-header-text">
					<h1>Logs PM2</h1>
					<p>Inspección de logs del proceso del backend</p>
				</div>
				<div className="logs-header-actions">
					<span className="refresh-badge">
						<span className="refresh-dot"></span>
						Actualizando cada 15s · { lastLabel }
					</span>
					<button
						className="dashboard-refresh-btn"
						onClick={ this.manualRefresh }
						disabled={ initialLoading }>
						{ initialLoading ? 'Cargando…' : 'Refrescar' }
					</button>
				</div>
			</div>
		);
	}

	renderTabs() {
		const { file } = this.state;
		return (
			<div className="log-tabs">
				<button
					className={ `log-tab ${ file === 'error' ? 'active' : '' }` }
					onClick={ () => this.switchFile('error') }>
					Error Log
				</button>
				<button
					className={ `log-tab ${ file === 'out' ? 'active' : '' }` }
					onClick={ () => this.switchFile('out') }>
					Out Log
				</button>
			</div>
		);
	}

	renderFilters() {
		const { search, dateFrom, dateTo, order, maxLines, eventsPerPage } = this.state;

		return (
			<div className="logs-filters">
				<div className="logs-filter-field logs-filter-search">
					<label>Buscar</label>
					<div className="logs-search-wrap">
						<span className="logs-search-icon" aria-hidden="true">⌕</span>
						<input
							type="text"
							className="filter-input"
							placeholder="Texto (case-insensitive)…"
							value={ search }
							onChange={ (e) => this.onSearchChange(e.target.value) }
						/>
					</div>
				</div>
				<div className="logs-filter-field">
					<label>Desde</label>
					<input
						type="datetime-local"
						className="filter-input"
						value={ dateFrom }
						onChange={ (e) => this.onDateChange('dateFrom', e.target.value) }
					/>
				</div>
				<div className="logs-filter-field">
					<label>Hasta</label>
					<input
						type="datetime-local"
						className="filter-input"
						value={ dateTo }
						onChange={ (e) => this.onDateChange('dateTo', e.target.value) }
					/>
				</div>
				<div className="logs-filter-field">
					<label>Orden</label>
					<select
						className="filter-select"
						value={ order }
						onChange={ (e) => this.onOrderChange(e.target.value as LogOrder) }>
						<option value="desc">Más recientes primero</option>
						<option value="asc">Más antiguos primero</option>
					</select>
				</div>
				<div className="logs-filter-field">
					<label>Máx. líneas</label>
					<select
						className="filter-select"
						value={ maxLines }
						onChange={ (e) => this.onMaxLinesChange(Number(e.target.value)) }>
						{ MAX_LINES_OPTIONS.map((n) => <option key={ n } value={ n }>{ formatNumber(n) }</option>) }
					</select>
					<span className="logs-filter-hint">Líneas leídas del archivo</span>
				</div>
				<div className="logs-filter-field">
					<label>Eventos por página</label>
					<select
						className="filter-select"
						value={ eventsPerPage }
						onChange={ (e) => this.onEventsPerPageChange(Number(e.target.value)) }>
						{ EVENTS_PER_PAGE_OPTIONS.map((n) => <option key={ n } value={ n }>{ n }</option>) }
					</select>
				</div>
				<button
					className="cron-btn cron-btn-ghost"
					onClick={ this.clearFilters }>
					Limpiar
				</button>
			</div>
		);
	}

	renderFileInfo() {
		const fi = this.state.data?.file_info;
		if (!fi) return null;

		const modified = fi.last_modified
			? moment(fi.last_modified).utcOffset(-4).format('DD/MM/YYYY HH:mm:ss')
			: '—';

		return (
			<div className="log-file-info">
				<div className="log-file-info__item log-file-info__path">
					<span className="info-label">Archivo</span>
					<code className="info-value mono ellipsis" title={ fi.path }>{ fi.path || '—' }</code>
				</div>
				<div className="log-file-info__item">
					<span className="info-label">Tamaño</span>
					<span className="info-value">{ formatBytes(fi.size_mb) }</span>
				</div>
				<div className="log-file-info__item">
					<span className="info-label">Modificado</span>
					<span className="info-value">{ modified }</span>
				</div>
			</div>
		);
	}

	// Calcula la ventana visible de grupos para la página actual.
	// Devuelve también totales para el footer/paginación. Centralizado
	// acá para que renderViewer y renderFooter consuman lo mismo y no
	// se desincronicen.
	getVisibleSlice() {
		const { data, page, eventsPerPage, order } = this.state;
		const lines = Array.isArray(data?.lines) ? data!.lines as Pm2LogLine[] : [];
		// 1) Agrupar respetando el orden de presentación (fix Issue 1:
		// la agrupación interna siempre se calcula en ASC, groupLines
		// se encarga de reinvertir si order=desc).
		// 2) Dedupe consecutivo (Issue 4): mismo mensaje N veces seguidas
		// colapsa a un solo grupo con duplicateCount>1.
		const allGroups = deduplicateConsecutive(groupLines(lines, order));
		const totalGroups = allGroups.length;
		const totalPages = Math.max(1, Math.ceil(totalGroups / eventsPerPage));
		// Si el log se achicó por refresh y la página actual quedó fuera
		// de rango, clampeamos al último válido sin tocar state (solo
		// para el cálculo de este render).
		const safePage = Math.min(Math.max(page, 1), totalPages);
		const startIdx = (safePage - 1) * eventsPerPage;
		const endIdx = startIdx + eventsPerPage;
		const visibleGroups = allGroups.slice(startIdx, endIdx);

		return {
			lines,
			allGroups,
			visibleGroups,
			totalGroups,
			totalPages,
			safePage,
			startIdx,
			endIdx: Math.min(endIdx, totalGroups),
		};
	}

	renderViewer() {
		const { data, loadError, initialLoading } = this.state;

		if (loadError) {
			return (
				<div className="cron-error-card">
					No se pudo cargar el archivo de logs. Reintentando en 15s…
				</div>
			);
		}

		if (initialLoading && !data) {
			return (
				<div className="log-viewer log-viewer--empty">
					<span>Cargando logs…</span>
				</div>
			);
		}

		const { lines, visibleGroups } = this.getVisibleSlice();

		if (lines.length === 0) {
			return (
				<div className="log-viewer log-viewer--empty">
					<span>No hay líneas para los filtros seleccionados.</span>
				</div>
			);
		}

		const { expandedGroups } = this.state;

		return (
			<div className="log-viewer">
				{
					visibleGroups.map((group, gIdx) => {
						const parent = group.parent;
						const lv = levelClass(parent.level);
						const childCount = group.children.length;
						const totalLines = 1 + childCount;
						const isExpanded = expandedGroups.has(gIdx);

						// Cuántas children mostramos: si el total ≤ 3, todas;
						// si > 3 y no está expandido, las 2 primeras; si está
						// expandido, todas. El toggle solo se renderiza cuando
						// hay children ocultas posibles (total > threshold).
						const showAll = totalLines <= GROUP_TOTAL_THRESHOLD || isExpanded;
						const visibleChildren = showAll
							? group.children
							: group.children.slice(0, COLLAPSED_CHILD_COUNT);
						const hiddenCount = childCount - visibleChildren.length;
						const showToggle = totalLines > GROUP_TOTAL_THRESHOLD;

						const parentRaw = stripAnsi(parent.raw || parent.preview || '');
						const parentTs = parent.timestamp
							? moment(parent.timestamp).utcOffset(-4).format('DD/MM HH:mm:ss')
							: '';

						// Dedupe pill "×N": tooltip con primera y última
						// ocurrencia (en orden ASC del archivo).
						const dupCount = group.duplicateCount;
						const showDupPill = dupCount > 1;
						const dupTimestamps = group.allTimestamps.filter(Boolean) as string[];
						const dupFirst = dupTimestamps[0]
							? moment(dupTimestamps[0]).utcOffset(-4).format('DD/MM HH:mm:ss')
							: '—';
						const dupLast = dupTimestamps[dupTimestamps.length - 1]
							? moment(dupTimestamps[dupTimestamps.length - 1]).utcOffset(-4).format('DD/MM HH:mm:ss')
							: '—';
						const dupTooltip = `Ocurrió ${ dupCount } veces. Primera: ${ dupFirst }. Última: ${ dupLast }`;

						// Pill "+N": cantidad de children ocultas/extras.
						// Solo aparece si hay children (childCount > 0).
						const showChildPill = childCount > 0;
						const childPillTooltip = `${ childCount } línea${ childCount === 1 ? '' : 's' } adicional${ childCount === 1 ? '' : 'es' } — Click para expandir`;

						return (
							<div
								className={ `log-event-group log-event-group--${ lv }` }
								key={ `g-${ parent.line_number ?? gIdx }-${ gIdx }` }>
								{ /* Parent */ }
								<div className={ `log-line ${ lv } ${ childCount > 0 ? 'is-parent' : '' }` }>
									<span className="log-line__number">{ parent.line_number ?? gIdx + 1 }</span>
									{ parentTs && <span className="log-line__timestamp">{ parentTs }</span> }
									<span className={ `log-line__level ${ lv }` }>{ (parent.level || '—').toString().toUpperCase() }</span>
									{
										showDupPill && (
											<span
												className={ `log-count-pill log-count-pill--dup log-count-pill--${ lv }` }
												title={ dupTooltip }
												aria-label={ dupTooltip }>
												×{ dupCount }
											</span>
										)
									}
									{
										showChildPill && (
											<span
												className={ `log-count-pill log-count-pill--child log-count-pill--${ lv }` }
												title={ childPillTooltip }
												aria-label={ childPillTooltip }
												role="button"
												onClick={ () => this.toggleGroup(gIdx) }>
												+{ childCount }
											</span>
										)
									}
									<span className="log-line__content">{ parentRaw }</span>
								</div>

								{ /* Visible children */ }
								{
									visibleChildren.map((child, cIdx) => {
										const childRaw = stripAnsi(child.raw || child.preview || '');
										return (
											<div
												className={ `log-line ${ lv } is-child` }
												key={ `g-${ gIdx }-c-${ child.line_number ?? cIdx }-${ cIdx }` }>
												<span className="log-line__number">{ child.line_number ?? '' }</span>
												<span className="log-line__content">{ childRaw }</span>
											</div>
										);
									})
								}

								{ /* Toggle "Ver X más" / "Ocultar" */ }
								{
									showToggle && (
										<div className={ `log-line ${ lv } is-child log-group-toggle-row` }>
											<span className="log-line__number"></span>
											<button
												type="button"
												className={ `log-group-toggle ${ lv }` }
												onClick={ () => this.toggleGroup(gIdx) }>
												{
													isExpanded
														? 'Ocultar'
														: `Ver ${ hiddenCount } línea${ hiddenCount === 1 ? '' : 's' } más`
												}
											</button>
										</div>
									)
								}
							</div>
						);
					})
				}
			</div>
		);
	}

	renderFooter() {
		const { data, loadError, initialLoading } = this.state;
		if (loadError || (initialLoading && !data)) return null;

		const { totalGroups, totalPages, safePage, startIdx, endIdx } = this.getVisibleSlice();
		if (totalGroups === 0) return null;

		const totalReturned = Number(data?.total_returned) || 0;
		const totalInFile = Number(data?.total_in_file) || 0;

		// Rango humano-friendly (1-indexed inclusivo).
		const fromIdx = totalGroups > 0 ? startIdx + 1 : 0;
		const toIdx = endIdx;

		return (
			<div className="logs-page__footer">
				<div className="logs-page__summary">
					Mostrando <strong>{ formatNumber(fromIdx) }</strong>–<strong>{ formatNumber(toIdx) }</strong> de{ ' ' }
					<strong>{ formatNumber(totalGroups) }</strong> evento{ totalGroups === 1 ? '' : 's' }
					{ totalReturned > 0 && (
						<> (de <strong>{ formatNumber(totalReturned) }</strong> líneas
						{ totalInFile > 0 && totalInFile !== totalReturned && (
							<> · <strong>{ formatNumber(totalInFile) }</strong> en archivo</>
						) })</>
					) }
				</div>
				{
					totalPages > 1 && (
						<div className="logs-page__pagination">
							<Pagination
								active={ safePage }
								pages={ totalPages }
								onChange={ (p: number) => this.goToPage(p) }
							/>
						</div>
					)
				}
			</div>
		);
	}

	render() {
		return (
			<div className="system-page logs-page">
				{ this.renderHeader() }
				{ this.renderTabs() }
				{ this.renderFilters() }
				{ this.renderFileInfo() }
				{ this.renderViewer() }
				{ this.renderFooter() }
			</div>
		);
	}
}

export default Logs;
